
































































import Vue from '@front-app/vendor/vue';
import {
	computed,
	defineComponent,
	nextTick,
	PropType,
	ref,
	Ref,
	watch,
} from '@vue/composition-api';
import { UserAvatarStore, UserStore } from '@front-app/store/singletons/User';
import { useThrottledUsersSearch } from '@front-app/hooks/ThrottledApi/userSearch';
import { PartialUser, UserSearchItem } from '@front-app/services/UserApi/UserModel';
import { getUsername } from '@front-app/util/user';
import { Size } from '@front-app/components/constants';
import DropdownDivider from '@front-app/components/dropdowns/DropdownDivider.vue';
import SearchBox from '../search/SearchBox.vue';
import SearchPlaceholder from '../search/SearchPlaceholder.vue';
import SearchResult from '../search/SearchResult.vue';
import { useRollingIndex } from '../search/useRollingIndex';
import {
	SearchOption,
	userFilter,
	userToSearchOption,
	useUserSearch,
} from '../search/useSearch';

export default defineComponent({
	components: {
		SearchBox,
		SearchPlaceholder,
		SearchResult,
		DropdownDivider,
	},
	props: {
		groupId: {
			type: String,
			required: true,
		},
		excludedUsers: {
			type: Array as PropType<string[]>,
			default: () => [],
		},
		preferredUsers: {
			type: Array as PropType<string[]>,
			default: () => [],
		},
		resultSize: {
			type: String as PropType<Size>,
			default: Size.MEDIUM,
		},
		preferredUserSubtitle: {
			type: String,
			default: '',
		},
	},
	setup(props, ctx) {
		watch(() => props.preferredUsers, (curr = []) => {
			UserStore.getInstance().fetchMany(curr);
		}, { immediate: true });
		const getUsernameInternal = (user: PartialUser) => getUsername(ctx.root.$t.bind(ctx.root), user);
		const { search: searchUsers, isLoading: isUserSearchLoading } = useThrottledUsersSearch();
		const apiUserSearchResults: Ref<UserSearchItem[]> = ref([]);
		const { searchString: userSearchString, results: userSearchResults } = useUserSearch(apiUserSearchResults);
		const filteredUserResults = computed(() => userSearchResults.value
			.filter(({ original }) => !props.excludedUsers.includes(original.id) && !props.preferredUsers.includes(original.id)));
		const el: Ref<null | Vue> = ref(null);
		const handleSearchItemClicked = async (user: SearchOption<UserSearchItem>): Promise<void> => {
			userSearchString.value = '';
			ctx.emit('select', user);
			const elVal = el.value;
			if (!elVal) {
				return;
			}
			await nextTick();
			elVal.$el.querySelector('input')?.focus();
		};
		const searchFocus = useRollingIndex(userSearchResults);
		const focusedSearchIdx = searchFocus.selectedIdx;
		const focusNextSearchResult = searchFocus.next;
		const focusPrevSearchResult = searchFocus.prev;
		const openFirstSearchResult = async () => {
			if (filteredUserResults.value.length === 0) {
				return;
			}
			await handleSearchItemClicked(filteredUserResults.value[0]);
		};
		const handleUserSearchInput = async (name: string) => {
			focusedSearchIdx.value = null;
			userSearchString.value = name;
			apiUserSearchResults.value = await searchUsers(props.groupId, { name, limit: 50 });
		};
		const visiblePreferredUsers = computed(() => props.preferredUsers.filter((id) => !props.excludedUsers.includes(id))
			.map(UserStore.getInstance().getPartial)
			.map(({ value }) => value)
			.map(userToSearchOption)
			.filter(({ original }) => userFilter(original, userSearchString)));
		return {
			el,
			handleSearchItemClicked,
			filteredUserResults,
			visiblePreferredUsers,
			userSearchString,
			getUsernameInternal,
			isUserSearchLoading,
			handleUserSearchInput,
			focusedSearchIdx,
			focusNextSearchResult,
			focusPrevSearchResult,
			openFirstSearchResult,
			// Avatar
			fetchAvatar: UserAvatarStore.fetch,
			getAvatar: UserAvatarStore.get,
		};
	},
});
